import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { NotificationService } from '../notification/notification.service';

@Injectable({
    providedIn: 'root',
})
export class RoomService {
    /**
     * Backing subject for [roomData]{@link RoomService#roomData}. roomData is
     * populated asynchronously (getRoomToken on the welcome page), so exposing
     * it as a stream lets consumers that may run before it resolves react to it.
     */
    private _roomData$ = new BehaviorSubject<any>({});

    /**
     * Emits each time [roomData]{@link RoomService#roomData} is (re)assigned.
     *  - Subscribed to in [setupAutoRecord]{@link SetupComponent#setupAutoRecord}
     *    so the auto-record default reflects account_config.auto_record even when
     *    the room details resolve after the setup form has been built.
     */
    readonly roomData$ = this._roomData$.asObservable();

    /**
     * The object containing the description, settings, role and token for the room.
     *  - Sets the [description]{@link RoomService#description}, [settings]{@link RoomService#settings}, [token]{@link RoomService#token}, [taskUrl]{@link RoomService#taskUrl}, and [taskInfo]{@link RoomService#taskInfo} getters
     *  - Used in the [AuthGuard]{@link AuthGuard} to check if there is room data
     *  - Set in [getRoomToken]{@link TaskSetupComponent#getRoomToken} in the TaskSetupComponent
     * - Set in [getRoomToken]{@link WelcomeComponent#getRoomToken} in the WelcomeComponent
     */
    get roomData(): any {
        return this._roomData$.value;
    }

    set roomData(value: any) {
        this._roomData$.next(value);
    }

    /**
     * Sets the roomId getter
     */
    private _roomId: string = '';

    isAiModerated = new BehaviorSubject(false);

    /**
     * Gets the roomId from the route params  of the activated route, in the welcome component.
     *  - Set in [setRoomId()]{@link TaskSetupComponent#setRoomId} in the TaskSetupComponent
     *  - Set in [setRoomId()]{@link WelcomeComponent#setRoomId} in the WelcomeComponent
     *  - Used in [updateUserDetails()]{@link UserService#updateUserDetails} in the UserService
     *  - Sets the [roomId]{@link SetupComponent#roomId} in the SetupComponent
     *  - Sets the [roomId]{@link TaskSetupComponent#roomId} in the TaskSetupComponent
     *  - Sets the [roomId]{@link WaitingRoomComponent#roomId} in the WaitingRoomComponent
     *  - Sets the [roomId]{@link WelcomeComponent#roomId} in the WelcomeComponent
     */
    public get roomId(): string {
        return this._roomId;
    }

    /**
     * Sets the roomId getter
     */
    public set roomId(value: string) {
        this._roomId = value;
    }

    /**
     * Gets the description from the [roomData]{@link RoomService#roomData} object.
     *  - Used in [updateUserDetails]{@link UserService#updateUserDetails}
     *  - Sets the [description]{@link RoomComponent#description} getter in the RoomComponent
     */
    get description(): string {
        return this.roomData.description;
    }

    /**
     * Gets the settings from the [roomData]{@link RoomService#roomData} object.
     *  - Possibly not being used.
     */
    get settings(): any {
        return this.roomData.settings;
    }

    /**
     * Gets the token from the [roomData]{@link RoomService#roomData} object.
     *  - Sets the [userConfig]{@link StreamProviderService#userConfig} in the StreamProviderService
     *  - Used in [getCallObject()]{@link StreamProviderService#getCallObject}
     */
    get token(): string {
        return this.roomData.token;
    }

    /**
     * Gets the task_url from the  [roomData]{@link RoomService#roomData} object.
     *  - Used to set [taskUrl]{@link RoomComponent#taskUrl} in the RoomComponent
     */
    get taskUrl(): string {
        return this.roomData.task_url;
    }

    /**
     * Gets the task_info from the [roomData]{@link RoomService#roomData} object.
     *  - Used to set [taskInfo]{@link RoomComponent#taskInfo} in the RoomComponent
     */
    get taskInfo(): string {
        return this.roomData.task_info;
    }

    /**
     * BehaviourSubject which is used to toggle the sidebar
     *  - Toggled in the [toggleRoomInfo()]{@link RoomService#toggleRoomInfo} and [toggleChat()]{@link RoomService#toggleChat} functions
     *  - Sets [sidebarActive]{@link RoomComponent#sidebarActive} getter in the RoomComponent
     *  - Subscribed to in [setupEvents]{@link RoomControlsComponent#setupEvents} in the RoomControlsComponent
     */
    sideBarActive = new BehaviorSubject(false);

    /**
     * Boolean which is used to toggle the roomInfo
     *  - Used in the [toggleRoomInfo()]{@link RoomService#toggleRoomInfo} function
     *  - Set to false in the [toggleChat()]{@link RoomService#toggleChat} function
     *  - Sets [roomInfoActive]{@link RoomComponent#roomInfoActive} in the RoomComponent
     *  - Sets [roomInfoActive]{@link RoomControlsComponent#roomInfoActive} in the RoomControlsComponent
     */
    roomInfoEnabled = false;

    /**
     * Boolean which is used to toggle the chat
     *  - Used in the [toggleChat()]{@link RoomService#toggleChat} function
     *  - Set to false in the [toggleRoomInfo()]{@link RoomService#toggleRoomInfo} function
     * - Sets [chatActive]{@link RoomComponent#chatActive} in the RoomComponent
     * - Sets [chatActive]{@link RoomControlsComponent#chatActive} in the RoomControlsComponent
     */
    chatEnabled = false;

    /**
     * Boolean which is used to toggle the transcript panel
     *  - Used in the [toggleTranscript()]{@link RoomService#toggleTranscript} function
     *  - Set to false in [toggleChat()]{@link RoomService#toggleChat} and [toggleRoomInfo()]{@link RoomService#toggleRoomInfo} (future — exclusive sidebar)
     *  - Sets [transcriptActive]{@link RoomComponent#transcriptActive} in the RoomComponent
     *  - Sets [transcriptEnabled]{@link RoomControlsComponent#transcriptEnabled} in the RoomControlsComponent
     */
    transcriptEnabled = false;

    /**
     * Boolean which is used to toggle the discussion guide panel
     *  - Used in the [toggleDiscussionGuide()]{@link RoomService#toggleDiscussionGuide} function
     *  - Set to false in [toggleChat()]{@link RoomService#toggleChat}, [toggleRoomInfo()]{@link RoomService#toggleRoomInfo} and [toggleTranscript()]{@link RoomService#toggleTranscript} (exclusive sidebar)
     *  - Sets [discussionGuideActive]{@link RoomComponent#discussionGuideActive} in the RoomComponent
     *  - Sets [discussionGuideEnabled]{@link RoomControlsComponent#discussionGuideEnabled} in the RoomControlsComponent
     */
    discussionGuideEnabled = false;

    /**
     * BehaviourSubject which is used to toggle the active settings
     *  - Toggled in the [toggleSettings()]{@link RoomService#toggleSettings} function
     *  - Sets [settingsActive]{@link RoomComponent#settingsActive} in the RoomComponent
     */
    settingsActive = new BehaviorSubject(false);
    troubleshootingActive = new BehaviorSubject(false);

    /**
     * Boolean which is used to determine if the task is task based
     *  - Set in [ngOnInit()]{@link TaskSetupComponent#ngOnInit} in the TaskSetupComponent
     *  - Used in [addParticipant()]{@link ParticipantService#addParticipant} in the ParticipantService
     *  - Used in [ngOnInit()]{@link CompleteComponent#ngOnInit} in the CompleteComponent
     *  - Sets [isTaskBased]{@link RoomComponent#isTaskBased} getter in the RoomComponent
     *  - Sets [isTaskBased]{@link RoomControlsComponent#isTaskBased} in the RoomControlsComponent
     *  - Sets [isTaskBased]{@link TaskSetupComponent#isTaskBased} in the TaskSetupComponent
     */
    isTaskBased = false;

    startTask = new Subject();

    popupError = new BehaviorSubject(false);

    isEmbedded = new BehaviorSubject(false);
    hasAppEvents = new BehaviorSubject(false);
    hasSpilloverRoom = new BehaviorSubject(true);

    isOwnSample = new BehaviorSubject<any>(null);

    spokenLanguage = '';
    captionLanguage = '';

    constructor(private notificationService: NotificationService) {}

    /**
     *  - Toggles the [roomInfoEnabled]{@link RoomService#roomInfoEnabled} boolean (which is used to display the room info)
     *  - Sets [chatEnabled]{@link RoomService#chatEnabled} boolean to false to hide the chat
     *  - If the side bar is not active/room info is not enabled, it toggles [sideBarActive]{@link RoomService#sideBarActive}
     *  - Called in [toggleRoomInfo()]{@link RoomControlsComponent#toggleRoomInfo} in the RoomControlsComponent
     *  - Called in [close()]{@link RoomDetailsComponent#close} in the RoomDetailsComponent
     * @returns void if the sidebar is active, it room info is enabled
     */
    toggleRoomInfo() {
        this.roomInfoEnabled = !this.roomInfoEnabled;
        this.chatEnabled = false;
        this.transcriptEnabled = false;
        this.discussionGuideEnabled = false;

        if (this.sideBarActive.value && this.roomInfoEnabled) {
            return;
        }

        this.sideBarActive.next(!this.sideBarActive.value);
    }

    /**
     *  - Toggles the [chatEnabled]{@link RoomService#chatEnabled} boolean
     *  - Sets [roomInfoEnabled]{@link RoomService#roomInfoEnabled} boolean to false
     *  - If the side bar is not active/chat is not enabled, it toggles [sideBarActive]{@link RoomService#sideBarActive}
     *  - Called in [toggleChat()]{@link RoomControlsComponent#toggleChat} in the RoomControlsComponent
     * @returns void if the sidebar is active and chat is enabled
     */
    toggleChat() {
        this.chatEnabled = !this.chatEnabled;
        this.roomInfoEnabled = false;
        this.transcriptEnabled = false;
        this.discussionGuideEnabled = false;

        if (this.sideBarActive.value && this.chatEnabled) {
            return;
        }

        this.sideBarActive.next(!this.sideBarActive.value);
    }

    /**
     *  - Toggles the [transcriptEnabled]{@link RoomService#transcriptEnabled} boolean
     *  - Sets [chatEnabled]{@link RoomService#chatEnabled} boolean to false (exclusive sidebar)
     *  - Sets [roomInfoEnabled]{@link RoomService#roomInfoEnabled} boolean to false (exclusive sidebar)
     *  - If the sidebar is not active / transcript is not enabled, toggles [sideBarActive]{@link RoomService#sideBarActive}
     *  - Called in [toggleTranscript]{@link RoomControlsComponent#toggleTranscript} in the RoomControlsComponent
     *  - Called in [close]{@link TranscriptPanelComponent#close} in the TranscriptPanelComponent
     * @returns void if the sidebar is active and transcript is enabled
     */
    toggleTranscript() {
        this.transcriptEnabled = !this.transcriptEnabled;
        this.chatEnabled = false;
        this.roomInfoEnabled = false;
        this.discussionGuideEnabled = false;

        if (this.sideBarActive.value && this.transcriptEnabled) {
            return;
        }

        this.sideBarActive.next(!this.sideBarActive.value);
    }

    /**
     *  - Toggles the [discussionGuideEnabled]{@link RoomService#discussionGuideEnabled} boolean
     *  - Sets [chatEnabled]{@link RoomService#chatEnabled}, [roomInfoEnabled]{@link RoomService#roomInfoEnabled} and [transcriptEnabled]{@link RoomService#transcriptEnabled} to false (exclusive sidebar)
     *  - If the sidebar is not active / the guide is not enabled, toggles [sideBarActive]{@link RoomService#sideBarActive}
     *  - Called in [toggleDiscussionGuide]{@link RoomControlsComponent#toggleDiscussionGuide} in the RoomControlsComponent
     *  - Called in [close]{@link DiscussionGuidePanelComponent#close} in the DiscussionGuidePanelComponent
     * @returns void if the sidebar is active and the guide is enabled
     */
    toggleDiscussionGuide() {
        this.discussionGuideEnabled = !this.discussionGuideEnabled;
        this.chatEnabled = false;
        this.roomInfoEnabled = false;
        this.transcriptEnabled = false;

        if (this.sideBarActive.value && this.discussionGuideEnabled) {
            return;
        }

        this.sideBarActive.next(!this.sideBarActive.value);
    }

    /**
     * Toggles the [settingsActive]{@link RoomService#settingsActive} BehaviourSubject
     *  - Called in [toggleSettings()]{@link RoomControlsComponent#toggleSettings} in the RoomControlsComponent
     * - Called in [closeSettings()]{@link SetupComponent#closeSettings} in the SetupComponent
     * @param value - boolean
     */
    toggleSettings(value?: boolean) {
        this.settingsActive.next(value !== undefined ? value : !this.settingsActive.value);
    }

    toggleTroubleshooting(value?: boolean) {
        this.troubleshootingActive.next(
            value !== undefined ? value : !this.troubleshootingActive.value,
        );
    }
}
